import type { ReactNode } from 'react';
import { Flex, FormLabel, Text } from '@chakra-ui/react';
import { StatusBadge, type StatusTone } from '../../../ui';

export const FieldLabel = ({ children, mb = 1.5 }: { children: ReactNode; mb?: number }) => (
    <FormLabel
        fontSize="sm"
        fontWeight="semibold"
        color="fg.default"
        mb={mb}
        me={0}
        display="flex"
        alignItems="baseline"
        requiredIndicator={
            <Text as="span" color="warning.fg" ml={0.5} aria-hidden>
                *
            </Text>
        }
    >
        {children}
    </FormLabel>
);

/** Small title-case marker beside a field label ("From catalog", "Inferred"). */
export const FieldBadge = ({
    tone = 'brand',
    children,
}: {
    tone?: StatusTone;
    children: ReactNode;
}) => (
    <StatusBadge
        tone={tone}
        textTransform="none"
        letterSpacing="normal"
        fontWeight="medium"
        fontSize="xs"
        lineHeight="short"
    >
        {children}
    </StatusBadge>
);

export interface FieldLabelWithBadgeProps {
    children: ReactNode;
    badge?: string | null;
    badgeTone?: StatusTone;
    isOptional?: boolean;
}

const FieldLabelWithBadge = ({
    children,
    badge,
    badgeTone,
    isOptional = false,
}: FieldLabelWithBadgeProps) => (
    <Flex align="center" gap={2} mb={1.5} minH={5}>
        <FieldLabel mb={0}>
            {children}
            {isOptional && (
                <Text as="span" textStyle="caption" color="fg.muted" fontWeight="normal" ml={1.5}>
                    Optional
                </Text>
            )}
        </FieldLabel>
        {badge && <FieldBadge tone={badgeTone}>{badge}</FieldBadge>}
    </Flex>
);

export default FieldLabelWithBadge;
